import React, { useEffect, useState } from "react";
import { Star, Save, Plus, Trash2, Globe, AlertCircle } from "lucide-react";
import { api, formatError } from "../../lib/api.js";

export default function SurveyTemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const openPreview = async () => {
    setErr("");
    try {
      const { data } = await api.post(`/surveys/templates/${selected.id}/preview`, {
        subject_pt: selected.subject_pt,
        intro_pt: selected.intro_pt,
        questions: selected.questions,
        scale: selected.scale,
      });
      setPreview(data);
      setShowPreview(true);
    } catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };

  const load = async () => {
    const { data } = await api.get("/surveys/templates");
    setTemplates(data);
    if (data.length && !selectedId) setSelectedId(data[0].id);
  };
  useEffect(() => { load(); }, []);

  const selected = templates.find((t) => t.id === selectedId);

  const updateField = (field, value) => {
    setTemplates((prev) => prev.map((t) => t.id === selectedId ? { ...t, [field]: value } : t));
  };

  const updateQuestion = (idx, patch) => {
    const q = [...(selected.questions || [])];
    q[idx] = { ...q[idx], ...patch };
    updateField("questions", q);
  };

  const addQuestion = () => {
    const q = [...(selected.questions || [])];
    q.push({
      id: `q${q.length + 1}`,
      text_pt: "",
      text_en: "",
      type: "score",
    });
    updateField("questions", q);
  };

  const removeQuestion = (idx) => {
    const q = [...(selected.questions || [])];
    q.splice(idx, 1);
    updateField("questions", q);
  };

  const save = async () => {
    setErr("");
    try {
      await api.patch(`/surveys/templates/${selected.id}`, {
        subject_pt: selected.subject_pt,
        subject_en: selected.subject_en,
        intro_pt: selected.intro_pt,
        intro_en: selected.intro_en,
        questions: selected.questions,
        active: selected.active,
      });
      setSavedAt(new Date().toLocaleTimeString());
      load();
    } catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-sm" data-testid="survey-templates-panel">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-[#1F3864]" />
          <h2 className="font-heading font-bold text-sm text-[#1A1A2E]">Templates de inquérito (NPS · CSI · CES)</h2>
        </div>
        <div className="text-xs text-[#6C757D]">{templates.length} templates</div>
      </div>

      <div className="flex flex-col md:flex-row">
        {/* Sidebar list */}
        <aside className="md:w-72 shrink-0 border-r border-gray-200" data-testid="survey-templates-list">
          <div className="p-2 space-y-1 max-h-[70vh] overflow-y-auto">
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                className={`w-full text-left px-3 py-2 rounded-sm text-sm flex items-center justify-between gap-2 ${selectedId === t.id ? "bg-[#1F3864] text-white" : "hover:bg-[#F4F6F8]"}`}
                data-testid={`survey-template-${t.survey_type}-${t.department}`}
              >
                <span>
                  <span className="font-mono text-[10px] uppercase tracking-widest mr-1">{t.survey_type}</span>
                  <span>· {t.department}</span>
                </span>
                {!t.active && <span className="text-[9px] bg-[#F8D7DA] text-[#721C24] px-1.5 py-0.5 rounded-sm">off</span>}
              </button>
            ))}
          </div>
        </aside>

        {/* Editor */}
        <main className="flex-1 min-w-0 p-4">
          {!selected ? (
            <div className="text-sm text-[#6C757D] italic text-center py-8">Selecciona um template à esquerda.</div>
          ) : (
            <div className="space-y-4" data-testid="survey-template-editor">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#1F3864] font-bold">{selected.survey_type} · {selected.department}</div>
                  <div className="text-xs text-[#6C757D] mt-1">Escala: {selected.scale}</div>
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={!!selected.active} onChange={(e) => updateField("active", e.target.checked)} data-testid="survey-template-active" />
                  Activo
                </label>
              </div>

              {/* Subject PT/EN */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Assunto email (PT)" icon="PT">
                  <input value={selected.subject_pt || ""} onChange={(e) => updateField("subject_pt", e.target.value)} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="survey-tpl-subject-pt" />
                </Field>
                <Field label="Subject email (EN)" icon="EN">
                  <input value={selected.subject_en || ""} onChange={(e) => updateField("subject_en", e.target.value)} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="survey-tpl-subject-en" />
                </Field>
              </div>

              {/* Intro PT/EN */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Introdução (PT)" icon="PT">
                  <textarea rows={2} value={selected.intro_pt || ""} onChange={(e) => updateField("intro_pt", e.target.value)} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="survey-tpl-intro-pt" />
                </Field>
                <Field label="Intro (EN)" icon="EN">
                  <textarea rows={2} value={selected.intro_en || ""} onChange={(e) => updateField("intro_en", e.target.value)} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="survey-tpl-intro-en" />
                </Field>
              </div>

              {/* Questions editor */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-heading text-sm font-bold text-[#1A1A2E]">Perguntas ({(selected.questions || []).length})</div>
                  <button onClick={addQuestion} className="text-xs bg-[#1F3864] text-white rounded-sm px-3 py-1.5 flex items-center gap-1" data-testid="survey-tpl-add-q">
                    <Plus className="w-3 h-3" /> Adicionar
                  </button>
                </div>

                <div className="space-y-3">
                  {(selected.questions || []).map((q, idx) => (
                    <div key={q.id || idx} className="border border-gray-200 rounded-sm p-3 bg-[#FAFBFC]" data-testid={`survey-tpl-q-${q.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <input value={q.id || ""} onChange={(e) => updateQuestion(idx, { id: e.target.value })} placeholder="ID (ex: q1)" className="w-20 border border-gray-300 rounded-sm px-2 py-1 text-xs font-mono" />
                        <select value={q.type || "score"} onChange={(e) => updateQuestion(idx, { type: e.target.value })} className="border border-gray-300 rounded-sm px-2 py-1 text-xs" data-testid={`survey-tpl-q-${idx}-type`}>
                          <option value="score">Pontuação</option>
                          <option value="text">Texto</option>
                        </select>
                        <button onClick={() => removeQuestion(idx)} className="ml-auto text-[#DC3545] hover:text-[#A51C2A]" data-testid={`survey-tpl-q-${idx}-remove`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <label className="block"><span className="text-[9px] uppercase tracking-widest text-[#6C757D]">Pergunta (PT)</span>
                          <textarea rows={2} value={q.text_pt || ""} onChange={(e) => updateQuestion(idx, { text_pt: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1 text-sm" data-testid={`survey-tpl-q-${idx}-text-pt`} />
                        </label>
                        <label className="block"><span className="text-[9px] uppercase tracking-widest text-[#6C757D]">Question (EN)</span>
                          <textarea rows={2} value={q.text_en || ""} onChange={(e) => updateQuestion(idx, { text_en: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1 text-sm" data-testid={`survey-tpl-q-${idx}-text-en`} />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm flex items-start gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />{err}</div>}

              <div className="pt-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-xs text-[#6C757D]">
                  {savedAt ? `Guardado às ${savedAt}` : "Alterações não guardadas serão perdidas."}
                </div>
                <div className="flex gap-2">
                  <button onClick={openPreview} className="border border-[#1F3864] text-[#1F3864] hover:bg-[#1F3864] hover:text-white rounded-sm px-3 py-2 text-sm font-semibold" data-testid="survey-tpl-preview">
                    Pré-visualizar email
                  </button>
                  <button onClick={save} className="bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm px-4 py-2 text-sm font-semibold flex items-center gap-2" data-testid="survey-tpl-save">
                    <Save className="w-4 h-4" /> Guardar template
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {showPreview && preview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="survey-tpl-preview-dialog">
          <div className="bg-white rounded-md border border-gray-300 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-sm">Pré-visualização do email</h3>
                <div className="text-xs text-[#6C757D] mt-0.5">Assunto: <b>{preview.subject}</b></div>
              </div>
              <button onClick={() => setShowPreview(false)} className="text-[#6C757D] hover:text-[#1A1A2E]" data-testid="survey-tpl-preview-close">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto bg-[#F4F6F8] p-4">
              <iframe
                title="email-preview"
                srcDoc={preview.html}
                className="w-full h-[500px] bg-white border border-gray-300 rounded-sm"
                data-testid="survey-tpl-preview-iframe"
              />
              {(preview.questions_preview || []).length > 0 && (
                <div className="mt-4 bg-white border border-gray-300 rounded-sm p-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-2">Perguntas do inquérito (escala {preview.scale})</div>
                  <ol className="list-decimal ml-5 space-y-1 text-sm">
                    {preview.questions_preview.map((q, i) => (
                      <li key={i}><b className="font-mono text-xs">[{q.type}]</b> {q.text_pt}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, icon, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D] flex items-center gap-1">
        <Globe className="w-3 h-3" /> {icon} · {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
